import { inject, Injectable } from '@angular/core';
import { FIREBASE_PROJECT_ID_PROVIDER } from '@principle-theorem/ng-shared';
import { isWithRef, snapshot } from '@principle-theorem/shared';
import { ManagementResource } from '../models/management-resource';
import { ManagementToolSidebarService } from '../management-tool-sidebar.service';
import {
  type IManagementTool,
  type IToolRunContext,
} from '../models/management-tool';

export function getStorageBucketLink(projectId: string, path: string): string {
  const bucketName = `${projectId}.appspot.com`;
  const encodedPath = path
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  const encodedProjectId = encodeURIComponent(projectId);
  return `https://console.cloud.google.com/storage/browser/${bucketName}/${encodedPath}?project=${encodedProjectId}`;
}

@Injectable({ providedIn: 'root' })
export class OpenStorageBucketTool implements IManagementTool {
  private _sidebarService = inject(ManagementToolSidebarService);
  private _projectId = inject(FIREBASE_PROJECT_ID_PROVIDER);
  private readonly _excludedResources = new Set([
    ManagementResource.CustomReport,
    ManagementResource.ReportRun,
  ]);

  readonly name = 'open-storage-bucket';
  readonly label = 'Open Storage Bucket';
  readonly icon = 'cloud';
  readonly description = 'Open the storage path in GCP Storage';
  readonly resource = ManagementResource.WithRef;

  async isHidden(context: IToolRunContext): Promise<boolean> {
    const showDevTools = await snapshot(this._sidebarService.showDevTools$);
    if (!showDevTools) {
      return true;
    }
    if (
      context.resource &&
      this._excludedResources.has(context.resource.resource.resource)
    ) {
      return true;
    }
    return false;
  }

  async run(context: IToolRunContext): Promise<void> {
    if (!context.resource) {
      return;
    }
    const value = await snapshot(context.resource.value$);
    if (!value || !isWithRef(value)) {
      return;
    }
    const url = getStorageBucketLink(this._projectId, value.ref.path);
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
